import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute, SubscriptionRoute } from "@/components/ProtectedRoute";
import InstallPrompt from "@/components/InstallPrompt";
import LandingPage from "@/pages/LandingPage";
import GetYourAuthority from "@/pages/GetYourAuthority";
import GlossaryPublic from "@/pages/GlossaryPublic";
import FAQPublic from "@/pages/FAQPublic";
import CPMCalculatorPublic from "@/pages/CPMCalculatorPublic";
import OwnerOperatorSalary from "@/pages/OwnerOperatorSalary";
import IFTACalculator from "@/pages/IFTACalculator";
import StartupCost from "@/pages/StartupCost";
import OwnerOperatorTaxes from "@/pages/OwnerOperatorTaxes";
import About from "@/pages/About";
import PlaybookHome from "@/pages/PlaybookHome";
import StepDetail from "@/pages/StepDetail";
import Funnel from "@/pages/Funnel";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Subscribe from "@/pages/Subscribe";
import LoadBoard from "@/pages/LoadBoard";
import CPMCalculator from "@/pages/CPMCalculator";
import PaperLogs from "@/pages/PaperLogs";
import Glossary from "@/pages/Glossary";
import GlossaryV2 from "@/pages/guidev2/GlossaryV2";
import AICoach from "@/pages/AICoach";
import ExportHub from "@/pages/ExportHub";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import GuideV2Dashboard from "@/pages/guidev2/GuideV2Dashboard";
import ModuleV2 from "@/pages/guidev2/ModuleV2";
import ProHub from "@/pages/guidev2/ProHub";
import ProToolPage from "@/pages/guidev2/ProToolPage";
import CommunityFeaturePage from "@/pages/guidev2/CommunityFeaturePage";

function AuthCallback() {
  const { googleExchange } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.replace("#", "")).get("session_id");
    (async () => {
      try { await googleExchange(sid); } catch {}
      window.history.replaceState(null, "", window.location.pathname);
      navigate("/", { replace: true });
    })();
  }, []); // eslint-disable-line
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <Loader2 className="w-8 h-8 text-brand animate-spin" />
    </div>
  );
}

// Logged-out visitors get the marketing landing page; members go straight to their guide.
function Home() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-brand animate-spin" />
      </div>
    );
  }
  return user ? <PlaybookHome /> : <LandingPage />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/how-to-get-your-own-authority" element={<GetYourAuthority />} />
      <Route path="/trucking-glossary" element={<GlossaryPublic />} />
      <Route path="/owner-operator-faq" element={<FAQPublic />} />
      <Route path="/cost-per-mile-calculator" element={<CPMCalculatorPublic />} />
      <Route path="/how-much-do-owner-operators-make" element={<OwnerOperatorSalary />} />
      <Route path="/ifta-fuel-tax-calculator" element={<IFTACalculator />} />
      <Route path="/how-much-does-it-cost-to-start-a-trucking-company" element={<StartupCost />} />
      <Route path="/owner-operator-taxes" element={<OwnerOperatorTaxes />} />
      <Route path="/about" element={<About />} />
      <Route path="/step/:n" element={<ProtectedRoute><StepDetail /></ProtectedRoute>} />
      <Route path="/diagnostic" element={<ProtectedRoute><Funnel /></ProtectedRoute>} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/subscribe" element={<ProtectedRoute><Subscribe /></ProtectedRoute>} />
      <Route path="/loadboard" element={<SubscriptionRoute><LoadBoard /></SubscriptionRoute>} />
      <Route path="/cpm" element={<SubscriptionRoute><CPMCalculator /></SubscriptionRoute>} />
      <Route path="/paper-logs" element={<SubscriptionRoute><PaperLogs /></SubscriptionRoute>} />
      <Route path="/glossary" element={<ProtectedRoute><Glossary /></ProtectedRoute>} />
      <Route path="/coach" element={<SubscriptionRoute><AICoach /></SubscriptionRoute>} />
      <Route path="/export" element={<ProtectedRoute><ExportHub /></ProtectedRoute>} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      {/* Version 2 — RouteHaul Pathfinder (active development, hidden from V1 nav) */}
      <Route path="/v2" element={<GuideV2Dashboard />} />
      <Route path="/v2/module/:n" element={<ModuleV2 />} />
      <Route path="/v2/pro" element={<ProHub />} />
      <Route path="/v2/pro/:key" element={<ProToolPage />} />
      <Route path="/v2/learn/:key" element={<CommunityFeaturePage />} />
      <Route path="/v2/glossary" element={<GlossaryV2 />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <InstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
